// The box that opens before Cobrain writes anything (module 04 D259 part 2 and answer 2; module 10
// DESIGN B1553, B1554, B1561). Mike's words: "There is no way that Cobrain could possibly know what
// to say, and so before writing anything it should have an input box for basic prompts to guide the
// LLM's response."
//
// Two things this gets right that the flow did not:
//
// 1. PRESSING THE LEAD RECORDS NOTHING. The card used to post the decision and then open the draft,
//    which records a decision he may never complete. Opening a box is not a decision (D60), so the
//    press is recorded when he presses THROUGH the box.
//
// 2. ONE LIVE BUTTON, always in the same place, whose LABEL says which of the two things it is.
//    The first drawing had `Write it` dead beside a live `Write it anyway`, and a cold reviewer
//    refused it: two buttons one word apart, and the one that looks disabled is the one whose name
//    you would press. So: empty box -> "Write it without an instruction"; words in it -> "Write it".
import { OptionButton, ActionButton, ActionTextarea } from '../parts/index.js';
import { useEffect, useRef, useState } from 'react';

export interface PromptBoxProps {
  /** One line saying where it is going: "To Ryan Walsh, by email, in the thread." Never a system as a source. */
  going: string;
  /** The option's own words, written into the box as its opening text -- visible and his to correct (B1554). */
  seed?: string | null;
  /** The words on the live button when the box has something in it. */
  writeLabel?: string;
  /**
   * The words when the box is EMPTY. The default suffixes the label, which is right for a reply --
   * "Write it without an instruction" -- and nonsense for anything whose label is already a
   * sentence: a decline read "Decline, and say this without an instruction" on the page, which is
   * how driving it caught what nine render tests could not.
   */
  emptyLabel?: string;
  /** A second way through, where the design gives one: declining with no sentence (B1561). */
  besides?: { label: string; onPress: () => void } | null;
  busy?: boolean;
  onWrite: (instruction: string | null) => void;
  onCancel: () => void;
}

export const PLACEHOLDER = 'What do you want to say? — “tell him I cannot make it, my flight leaves at six”';
export const NOTHING_SENT = 'Nothing is sent. Cobrain writes it and you read it first.';

/** The live button's words. Empty box and typed box are two different things, said in one place. */
export function writeWord(instruction: string, writeLabel: string, emptyLabel?: string): string {
  if (instruction.trim().length > 0) return writeLabel;
  return emptyLabel ?? `${writeLabel} without an instruction`;
}

export function PromptBox({ going, seed, writeLabel = 'Write it', emptyLabel, besides, busy = false, onWrite, onCancel }: PromptBoxProps) {
  const [instruction, setInstruction] = useState(seed ?? '');
  const box = useRef<HTMLTextAreaElement>(null);
  // The cursor is in it on open (D259 answer 2). With the option's own words seeded, the caret goes
  // to the END of them, so typing continues his sentence instead of cutting into it.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const write = () => { if (!busy) onWrite(instruction.trim().length > 0 ? instruction.trim() : null); };

  return (
    <div className="state" data-state="reply">
      <p className="rule">{going}</p>
      <ActionTextarea
        ref={box}
        className="draft"
        aria-label="what to say"
        placeholder={PLACEHOLDER}
        value={instruction}
        disabled={busy}
        rows={3}
        onChange={(e) => setInstruction(e.target.value)}
        onKeyDown={(e) => {
          // Enter alone makes a new line: this is a sentence, not a search box.
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); write(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
      />
      <div className="teach-block">
        <OptionButton variant="lead" className="go" disabled={busy} onClick={write}>{writeWord(instruction, writeLabel, emptyLabel)}</OptionButton>
        {besides ? <OptionButton className="out far" disabled={busy} onClick={besides.onPress}>{besides.label}</OptionButton> : null}
        <ActionButton reading type="button" className="link" disabled={busy} onClick={onCancel}>Cancel</ActionButton>
      </div>
      <p className="note">{NOTHING_SENT}</p>
    </div>
  );
}
